import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { admin } from "@/lib/api";
import { toast } from "sonner";
import { Users, Bell, UserPlus, ArrowLeft } from "lucide-react";

const NIVEIS = ["admin", "diretor", "compliance", "rh", "supervisor", "gerente", "operacional"];
const SETORES = ["diretoria", "compliance", "rh", "operacoes", "administrativo", "vendas", "financeiro", "ti", "marketing", "juridico"];

export default function AdminUsuarios() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("usuarios");
  const [users, setUsers] = useState([]);
  const [solic, setSolic] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ nome: "", email: "", password: "", nivel: "operacional", setor: "operacoes" });

  useEffect(() => { load(); }, [tab]);
  const load = async () => {
    try {
      if (tab === "usuarios") setUsers((await admin.users()).data);
      if (tab === "solicitacoes") setSolic((await admin.solicitacoes()).data);
    } catch { toast.error("Sem permissão"); }
  };

  const suspender = async (id) => {
    try {
      const r = await admin.suspender(id);
      toast.success(`Status alterado para ${r.data.novo_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const criar = async () => {
    if (!form.nome || !form.email || !form.password) return toast.error("Preencha nome, email e senha");
    try {
      await admin.criarUsuario(form);
      toast.success("Usuário criado");
      setForm({ nome: "", email: "", password: "", nivel: "operacional", setor: "operacoes" });
      setTab("usuarios");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const filtered = users.filter(u => !q || u.nome?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()));

  const setorColor = (s) => ({diretoria:"bg-blue-100 text-blue-700", compliance:"bg-amber-100 text-amber-700", rh:"bg-purple-100 text-purple-700", operacoes:"bg-cyan-100 text-cyan-700", ti:"bg-sky-100 text-sky-700", vendas:"bg-emerald-100 text-emerald-700", financeiro:"bg-teal-100 text-teal-700", marketing:"bg-pink-100 text-pink-700", administrativo:"bg-slate-100 text-slate-700", juridico:"bg-red-100 text-red-700"})[s] || "bg-slate-100 text-slate-700";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952]">
        <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center text-[#B28F4B] font-bold text-xs">SOE</div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold text-lg font-heading">SOEASY Admin</div>
          </div>
          <div className="text-white/80 text-xs">{user?.nome}</div>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-white/25 text-white text-xs font-bold uppercase tracking-widest">← Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">Sair</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 pt-5">
        <div className="flex gap-1 border-b border-slate-200">
          {[["usuarios", "Usuários", Users], ["solicitacoes", "Solicitações", Bell], ["novo", "Novo Usuário", UserPlus]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 flex items-center gap-1.5 ${tab===id?"border-[#B28F4B] text-[#B28F4B]":"border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon size={13}/> {label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto p-5">
        {tab === "usuarios" && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-2 text-[#1A1D42] font-bold"><Users size={16}/> Usuários do Sistema</div>
              <input placeholder="Buscar..." value={q} onChange={e=>setQ(e.target.value)} data-testid="input-buscar-user" className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"/>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <tr><th className="p-3">Nome</th><th className="p-3">E-mail</th><th className="p-3">Setor</th><th className="p-3">Nível</th><th className="p-3">Status</th><th className="p-3">Último Acesso</th><th className="p-3">Ações</th></tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-800">{u.nome}</td>
                    <td className="p-3 text-slate-600 text-xs">{u.email}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${setorColor(u.setor)}`}>{u.setor}</span></td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">{u.nivel}</span></td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${u.status==="ativo"?"bg-emerald-100 text-emerald-700":"bg-red-100 text-red-700"}`}>{u.status}</span></td>
                    <td className="p-3 text-slate-500 text-xs font-mono">{u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleDateString("pt-BR") : "—"}</td>
                    <td className="p-3">
                      <button onClick={() => suspender(u.id)} data-testid={`btn-suspender-${u.id}`} className={`px-3 py-1 rounded text-[10px] font-bold uppercase ${u.status==="ativo"?"bg-red-100 text-red-700 hover:bg-red-200":"bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
                        {u.status === "ativo" ? "Suspender" : "Reativar"}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-slate-400 text-xs">Nenhum usuário.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "solicitacoes" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="text-[#1A1D42] font-bold flex items-center gap-2 mb-4"><Bell size={16}/> Solicitações</div>
            {solic.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-sm">Nenhuma solicitação pendente.</div>
            ) : (
              <div className="space-y-2">{solic.map(s => (<div key={s.id} className="p-3 border border-slate-200 rounded-lg"><div className="font-bold text-sm">{s.assunto}</div><div className="text-xs text-slate-500">{s.solicitante}</div></div>))}</div>
            )}
          </div>
        )}

        {tab === "novo" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm max-w-2xl">
            <div className="text-[#1A1D42] font-bold flex items-center gap-2 mb-4"><UserPlus size={16}/> Novo Usuário</div>
            <div className="space-y-3">
              <input placeholder="Nome completo" value={form.nome} onChange={e=>setForm({...form, nome: e.target.value})} data-testid="input-novo-nome" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
              <input type="email" placeholder="E-mail" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="input-novo-email" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
              <input type="password" placeholder="Senha" value={form.password} onChange={e=>setForm({...form, password: e.target.value})} data-testid="input-novo-senha" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.nivel} onChange={e=>setForm({...form, nivel: e.target.value})} data-testid="select-novo-nivel" className="px-3 py-2 rounded-lg border border-slate-200 text-sm">{NIVEIS.map(n=><option key={n}>{n}</option>)}</select>
                <select value={form.setor} onChange={e=>setForm({...form, setor: e.target.value})} data-testid="select-novo-setor" className="px-3 py-2 rounded-lg border border-slate-200 text-sm">{SETORES.map(s=><option key={s}>{s}</option>)}</select>
              </div>
              <button onClick={criar} data-testid="btn-criar-usuario" className="px-5 py-2 rounded-lg bg-[#1A1D42] hover:bg-[#252952] text-white font-bold text-xs uppercase tracking-widest">✓ Criar Usuário</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
